import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, ChevronDown, FileCheck2, Loader2, ShieldCheck, XCircle } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Checkbox } from '../../components/ui/checkbox';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Progress } from '../../components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import LogoLockup from '../../components/shared/LogoLockup';
import AdirePattern from '../../components/shared/AdirePattern';
import { NIGERIA_STATES, getLgasForState, isValidLgaForState } from '../../lib/nigeriaLocations';
import { getApiErrorMessage } from '../../lib/apiError';

const today = () => new Date().toISOString().slice(0, 10);

const subscriberInitialForm = {
  title: '',
  firstName: '',
  middleName: '',
  lastName: '',
  dateOfBirth: '',
  gender: '',
  nationality: '',
  primaryEmail: '',
  alternateEmail: '',
  primaryPhone: '',
  whatsappNumber: '',
  preferredContactMethod: '',
  countryOfResidence: '',
  city: '',
  stateRegion: '',
  residentialAddress: '',
  postalCode: '',
  nin: '',
  validIdUpload: null,
  proofOfResidenceUpload: null,
  passportPhotoUpload: null,
  selectedPackage: '',
  billingCycle: '',
  paymentMethod: '',
  autoRenewalConsent: '',
  declarationFullName: '',
  declarationSignature: '',
  declarationDate: today(),
  agreedToTerms: false
};

const supervisorInitialForm = {
  title: '',
  firstName: '',
  middleName: '',
  lastName: '',
  dateOfBirth: '',
  age: '',
  gender: '',
  nationality: '',
  primaryPhone: '',
  secondaryPhone: '',
  whatsappNumber: '',
  email: '',
  street: '',
  neighbourhood: '',
  lga: '',
  state: '',
  landmark: '',
  nin: '',
  bvn: '',
  primaryIdUpload: null,
  secondIdUpload: null,
  passportPhotoUpload: null,
  proofOfAddressUpload: null,
  highestQualification: '',
  institutionName: '',
  courseOfStudy: '',
  yearOfGraduation: '',
  certificateUpload: null,
  yearsExperience: '',
  currentEmployer: '',
  jobTitle: '',
  employmentDuration: '',
  reasonForLeaving: '',
  hasFirstAidTraining: '',
  firstAidCertification: '',
  workCertificatesUpload: null,
  preferredTerritory: '',
  nokFullName: '',
  nokRelationship: '',
  nokPhone: '',
  nokEmail: '',
  consentAccuracy: false,
  consentBackgroundCheck: false,
  consentFalseInfoTermination: false,
  consentCodeOfConduct: false,
  consentDataRetention: false,
  declarationFullName: '',
  declarationSignature: '',
  declarationDate: today()
};

const subscriberSections = [
  {
    title: 'Personal Info',
    fields: [
      { key: 'title', label: 'Title', type: 'select', options: ['Mr', 'Mrs', 'Ms', 'Dr', 'Prof', 'Other'] },
      { key: 'firstName', label: 'First Name', required: true },
      { key: 'middleName', label: 'Middle Name' },
      { key: 'lastName', label: 'Last Name', required: true },
      { key: 'dateOfBirth', label: 'Date of Birth', type: 'date', required: true },
      { key: 'gender', label: 'Gender', type: 'select', options: ['Male', 'Female', 'Prefer not to say'], required: true },
      { key: 'nationality', label: 'Nationality', required: true }
    ]
  },
  {
    title: 'Contact',
    fields: [
      { key: 'primaryEmail', label: 'Primary Email', type: 'email', required: true },
      { key: 'alternateEmail', label: 'Alternate Email', type: 'email' },
      { key: 'primaryPhone', label: 'Primary Phone', type: 'tel', required: true, hint: 'Include country code.' },
      { key: 'whatsappNumber', label: 'WhatsApp Number', type: 'tel' },
      { key: 'preferredContactMethod', label: 'Preferred Contact Method', type: 'select', options: ['Email', 'Phone Call', 'WhatsApp', 'SMS'], required: true }
    ]
  },
  {
    title: 'Residence',
    fields: [
      { key: 'countryOfResidence', label: 'Country of Residence', required: true },
      { key: 'city', label: 'City', required: true },
      { key: 'stateRegion', label: 'State / Region / Province', required: true },
      { key: 'residentialAddress', label: 'Full Residential Address', type: 'textarea', required: true },
      { key: 'postalCode', label: 'Postal / ZIP Code' }
    ]
  },
  {
    title: 'Identity',
    fields: [
      { key: 'nin', label: 'National Identification Number (NIN)', required: true, hint: 'Enter exactly 11 digits.' },
      { key: 'validIdUpload', label: 'Upload: Valid Nigerian ID', type: 'file', required: true },
      { key: 'proofOfResidenceUpload', label: 'Upload: Proof of Overseas Residence', type: 'file', required: true, hint: 'Proof of residence: utility bill, bank statement, or government letter not older than 3 months.' },
      { key: 'passportPhotoUpload', label: 'Upload: Recent Passport Photograph', type: 'file', required: true }
    ]
  },
  {
    title: 'Package',
    fields: [
      { key: 'selectedPackage', label: 'Selected Package', type: 'package-cards', required: true },
      { key: 'billingCycle', label: 'Billing Cycle', type: 'select', options: ['Monthly', 'Quarterly', 'Annually'], required: true },
      { key: 'paymentMethod', label: 'Payment Method', type: 'select', options: ['Credit/Debit Card', 'Bank Transfer', 'PayPal', 'Other'], required: true },
      { key: 'autoRenewalConsent', label: 'Auto-Renewal Consent', type: 'select', options: ['Yes', 'No (notify before renewal)'], required: true }
    ]
  },
  {
    title: 'Declaration',
    fields: [
      { key: 'declarationFullName', label: 'Full Name (as signed)', required: true },
      { key: 'declarationSignature', label: 'Signature', required: true, hint: 'Typed signature is accepted for MVP.' },
      { key: 'declarationDate', label: 'Date', type: 'date', required: true, disabled: true },
      { key: 'agreedToTerms', label: 'Agree to Terms & Privacy Policy', type: 'checkbox', required: true }
    ]
  }
];

const supervisorSections = [
  {
    title: 'Personal Info',
    fields: [
      { key: 'title', label: 'Title', type: 'select', options: ['Mr', 'Mrs', 'Ms', 'Dr', 'Other'] },
      { key: 'firstName', label: 'First Name', required: true },
      { key: 'middleName', label: 'Middle Name' },
      { key: 'lastName', label: 'Last Name', required: true },
      { key: 'dateOfBirth', label: 'Date of Birth', type: 'date' },
      { key: 'age', label: 'Age', type: 'number', required: true },
      { key: 'gender', label: 'Gender', type: 'select', options: ['Male', 'Female'], required: true },
      { key: 'nationality', label: 'Nationality', required: true }
    ]
  },
  {
    title: 'Contact',
    fields: [
      { key: 'primaryPhone', label: 'Primary Phone Number', type: 'tel', required: true },
      { key: 'secondaryPhone', label: 'Secondary Phone Number', type: 'tel' },
      { key: 'whatsappNumber', label: 'WhatsApp Number', type: 'tel', required: true },
      { key: 'email', label: 'Email Address', type: 'email', required: true }
    ]
  },
  {
    title: 'Residential Address',
    fields: [
      { key: 'street', label: 'House Number & Street', required: true },
      { key: 'neighbourhood', label: 'Area / Neighbourhood', required: true },
      { key: 'state', label: 'State', type: 'state', required: true },
      { key: 'lga', label: 'Local Government Area (LGA)', type: 'lga' },
      { key: 'landmark', label: 'Nearest Landmark' }
    ]
  },
  {
    title: 'Identity',
    fields: [
      { key: 'nin', label: 'NIN', required: true, hint: 'Enter exactly 11 digits.' },
      { key: 'bvn', label: 'BVN', required: true, hint: 'Enter exactly 11 digits.' },
      { key: 'primaryIdUpload', label: 'Upload: Primary Valid ID', type: 'file', required: true },
      { key: 'secondIdUpload', label: 'Upload: Second Valid ID', type: 'file' },
      { key: 'passportPhotoUpload', label: 'Upload: Passport Photograph', type: 'file', required: true },
      { key: 'proofOfAddressUpload', label: 'Upload: Proof of Residential Address', type: 'file', required: true }
    ]
  },
  {
    title: 'Education',
    fields: [
      { key: 'highestQualification', label: 'Highest Qualification', type: 'select', options: ['SSCE/WAEC', 'OND', 'HND', 'B.Sc/B.A', 'M.Sc/MBA', 'PhD', 'Other'], required: true },
      { key: 'institutionName', label: 'Institution Name' },
      { key: 'courseOfStudy', label: 'Course of Study' },
      { key: 'yearOfGraduation', label: 'Year of Graduation', type: 'text' },
      { key: 'certificateUpload', label: 'Upload: Certificate / Result Slip', type: 'file', required: true }
    ]
  },
  {
    title: 'Work Experience',
    fields: [
      { key: 'yearsExperience', label: 'Total Years of Relevant Experience', type: 'number', required: true },
      { key: 'currentEmployer', label: 'Current/Most Recent Employer', required: true },
      { key: 'jobTitle', label: 'Job Title Held', required: true },
      { key: 'employmentDuration', label: 'Duration of Employment', required: true },
      { key: 'reasonForLeaving', label: 'Reason for Leaving', type: 'textarea' },
      { key: 'hasFirstAidTraining', label: 'Any caregiving or first aid training?', type: 'select', options: ['Yes', 'No'], required: true },
      { key: 'firstAidCertification', label: 'If Yes, specify certification' },
      { key: 'workCertificatesUpload', label: 'Upload: Relevant certificates', type: 'file' }
    ]
  },
  {
    title: 'Assignment',
    fields: [
      { key: 'preferredTerritory', label: 'Preferred Territory: City & State', required: true }
    ]
  },
  {
    title: 'Next of Kin',
    fields: [
      { key: 'nokFullName', label: 'Full Name', required: true },
      { key: 'nokRelationship', label: 'Relationship', type: 'select', options: ['Spouse', 'Parent', 'Sibling', 'Child', 'Friend', 'Other'], required: true },
      { key: 'nokPhone', label: 'Phone Number', type: 'tel', required: true },
      { key: 'nokEmail', label: 'Email Address', type: 'email', required: true }
    ]
  },
  {
    title: 'Declaration',
    fields: [
      { key: 'consentAccuracy', label: 'Confirm all info is true and accurate', type: 'checkbox', required: true },
      { key: 'consentBackgroundCheck', label: 'Consent to background checks and reference contact', type: 'checkbox', required: true },
      { key: 'consentFalseInfoTermination', label: 'Understand false info = immediate termination', type: 'checkbox', required: true },
      { key: 'consentCodeOfConduct', label: 'Agree to Code of Conduct and Confidentiality Policy', type: 'checkbox', required: true },
      { key: 'consentDataRetention', label: 'Authorise Magnolia to retain info for operations', type: 'checkbox', required: true },
      { key: 'declarationFullName', label: 'Full Name (print)', required: true },
      { key: 'declarationSignature', label: 'Signature', required: true },
      { key: 'declarationDate', label: 'Date', type: 'date', required: true, disabled: true }
    ]
  }
];

const configs = {
  subscriber: {
    title: "Welcome. Let's get to know you.",
    initialForm: subscriberInitialForm,
    sections: subscriberSections,
    getEndpoint: '/kyc/subscriber',
    saveEndpoint: '/kyc/subscriber',
    submitEndpoint: '/kyc/subscriber/submit',
    uploadEndpoint: '/kyc/upload'
  },
  supervisor: {
    title: 'Supervisor KYC',
    initialForm: supervisorInitialForm,
    sections: supervisorSections,
    getEndpoint: '/kyc/supervisor',
    saveEndpoint: '/kyc/supervisor',
    submitEndpoint: '/kyc/supervisor/submit',
    uploadEndpoint: '/kyc/supervisor/upload'
  }
};

const PACKAGE_OPTIONS = [
  {
    name: 'Essential',
    price: 'N/A',
    cadence: '1 visit/week',
    tone: 'cream',
    summary: 'A light care rhythm for families who need regular eyes at home.',
    included: ['Structured wellness visits', 'Subscriber dashboard', 'Supervisor-reviewed reports', 'Photo evidence'],
    excluded: ['Daily visits', 'Premium video evidence']
  },
  {
    name: 'Comfort',
    price: 'N/A',
    cadence: '3 visits/week',
    tone: 'wash',
    summary: 'A stronger weekly rhythm for families who want closer monitoring.',
    included: ['Three weekly visits', 'Alerts and care expenses', 'Care officer chat', 'Photo evidence'],
    excluded: ['Daily visits', 'Premium video evidence']
  },
  {
    name: 'Prestige',
    price: 'N/A',
    cadence: 'Daily visits',
    tone: 'burgundy',
    summary: 'The highest-touch plan for dependants who need daily visibility.',
    included: ['Daily visit coverage', 'Premium physical-condition evidence', 'Priority alerts', 'Care expense approvals'],
    excluded: []
  }
];

const packageToneClasses = {
  cream: 'border-[#E8E6E3] bg-[#F7F4EE] text-[#1A1A1A]',
  wash: 'border-[#E3C9CE] bg-[#F5E8EA] text-[#1A1A1A]',
  burgundy: 'border-[#9F1D35] bg-[#9F1D35] text-[#FAFAF7]'
};

const isFilled = (value, field) => {
  if (field.type === 'checkbox') return value === true;
  if (field.type === 'file') return Boolean(value?.fileUrl);
  if (field.type === 'multi') return Array.isArray(value) && value.length > 0;
  if (value === 0) return true;
  if (value === null || value === undefined) return false;
  return String(value).trim() !== '';
};

const KycPage = () => {
  const navigate = useNavigate();
  const { authApi, refreshUser, user } = useAuth();
  const config = configs[user?.role] || configs.subscriber;
  const { initialForm, sections } = config;
  const [form, setForm] = useState(initialForm);
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState({});
  const [uploadProgress, setUploadProgress] = useState({});
  const [error, setError] = useState('');
  const [missingFields, setMissingFields] = useState([]);
  const [submittedMessage, setSubmittedMessage] = useState('');
  const [kycStatus, setKycStatus] = useState(user?.kycStatus || 'not_started');
  const [expandedPackage, setExpandedPackage] = useState('Essential');

  const section = sections[currentStep];
  const completedSteps = useMemo(() => sections.map(item => (
    item.fields.filter(field => field.required).every(field => isFilled(form[field.key], field))
  )), [form, sections]);

  useEffect(() => {
    let mounted = true;
    const loadDraft = async () => {
      setLoading(true);
      try {
        const response = await authApi.get(config.getEndpoint);
        if (!mounted) return;
        const data = response.data || {};
        const merged = {
          ...initialForm,
          firstName: user?.profile?.first_name || user?.profile?.firstName || '',
          lastName: user?.profile?.last_name || user?.profile?.lastName || '',
          primaryEmail: user?.email || '',
          email: user?.email || '',
          primaryPhone: user?.profile?.phone || user?.profile?.primaryPhone || '',
          ...data,
          declarationDate: data.declarationDate || today()
        };
        setForm(merged);
        setKycStatus(data.kycStatus || user?.kycStatus || 'not_started');
        const firstIncomplete = sections.findIndex(item => (
          !item.fields.filter(field => field.required).every(field => isFilled(merged[field.key], field))
        ));
        setCurrentStep(firstIncomplete >= 0 ? firstIncomplete : sections.length - 1);
      } catch (err) {
        setError(getApiErrorMessage(err, 'Unable to load your KYC draft.'));
      } finally {
        if (mounted) setLoading(false);
      }
    };
    loadDraft();
    return () => {
      mounted = false;
    };
  }, [authApi, config.getEndpoint, initialForm, sections, user]);

  const updateField = (key, value) => {
    setForm(prev => {
      if (key === 'state') {
        return {
          ...prev,
          state: value,
          lga: isValidLgaForState(value, prev.lga) ? prev.lga : ''
        };
      }
      return { ...prev, [key]: value };
    });
    setMissingFields([]);
  };

  const saveSection = async ({ advance = true } = {}) => {
    setError('');
    setSaving(true);
    try {
      const payload = section.fields.reduce((acc, field) => {
        acc[field.key] = form[field.key];
        return acc;
      }, {});
      const response = await authApi.put(config.saveEndpoint, payload);
      setForm(prev => ({ ...prev, ...response.data, declarationDate: response.data.declarationDate || prev.declarationDate }));
      setKycStatus(response.data.kycStatus || kycStatus);
      if (advance) setCurrentStep(step => Math.min(step + 1, sections.length - 1));
      return true;
    } catch (err) {
      setError(getApiErrorMessage(err, 'Unable to save this section.'));
      return false;
    } finally {
      setSaving(false);
    }
  };

  const uploadFile = async (field, file) => {
    if (!file) return;
    setError('');
    const allowed = ['image/jpeg', 'image/png', 'application/pdf'];
    const extensionAllowed = /\.(jpe?g|png|pdf)$/i.test(file.name);
    if (!allowed.includes(file.type) && !extensionAllowed) {
      setError('Upload .jpg, .jpeg, .png, or .pdf files only.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError('Each upload must be 5MB or smaller.');
      return;
    }

    const data = new FormData();
    data.append('fieldName', field.key);
    data.append('file', file);

    setUploading(prev => ({ ...prev, [field.key]: true }));
    setUploadProgress(prev => ({ ...prev, [field.key]: 0 }));
    try {
      const response = await authApi.post(config.uploadEndpoint, data, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: event => {
          if (event.total) {
            setUploadProgress(prev => ({ ...prev, [field.key]: Math.round((event.loaded * 100) / event.total) }));
          }
        }
      });
      updateField(field.key, response.data);
    } catch (err) {
      setError(getApiErrorMessage(err, `Unable to upload ${field.label}.`));
    } finally {
      setUploading(prev => ({ ...prev, [field.key]: false }));
    }
  };

  const handleSubmit = async () => {
    setError('');
    setSubmittedMessage('');
    setSubmitting(true);
    try {
      const finalForm = { ...form, declarationDate: form.declarationDate || today() };
      setForm(finalForm);
      await authApi.put(config.saveEndpoint, finalForm);
      const response = await authApi.post(config.submitEndpoint);
      const updatedUser = await refreshUser();
      if (response.data.registered || updatedUser?.registered) {
        navigate('/dashboard', { replace: true });
        return;
      }
      setKycStatus(response.data.kycStatus || 'submitted');
      setSubmittedMessage(response.data.message || 'KYC submitted and awaiting review.');
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail?.missingFields) {
        setMissingFields(detail.missingFields);
        setError(detail.message || 'KYC submission is incomplete.');
      } else {
        setError(getApiErrorMessage(err, 'Unable to submit KYC.'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const renderField = (field) => {
    const value = form[field.key];
    const isMissing = missingFields.includes(field.label);
    const commonClass = isMissing ? 'border-red-300 focus-visible:ring-red-400' : '';

    if (field.type === 'package-cards') {
      return (
        <div className="space-y-4 sm:col-span-2" key={field.key}>
          <div>
            <Label>{field.label}{field.required ? ' *' : ''}</Label>
            <p className="mt-1 text-sm text-[#5A5A5A]">Choose the care rhythm that fits your family. Prices are marked N/A until Magnolia confirms final package rates.</p>
          </div>
          <div className="grid gap-4 lg:grid-cols-3">
            {PACKAGE_OPTIONS.map(option => {
              const selected = value === option.name;
              const isDark = option.tone === 'burgundy';
              const expanded = expandedPackage === option.name;
              return (
                <article
                  key={option.name}
                  className={`relative overflow-hidden rounded-2xl border p-5 transition-colors ${packageToneClasses[option.tone]} ${selected ? 'ring-2 ring-[#C9A961]' : ''}`}
                >
                  <AdirePattern className={`absolute inset-0 h-full w-full ${isDark ? 'text-[#FAFAF7]' : 'text-[#9F1D35]'}`} opacity={isDark ? 0.1 : 0.055} />
                  <div className="relative">
                    <div className="mb-4 flex items-start justify-between gap-3">
                      <div>
                        <h3 className={`font-serif text-2xl font-semibold ${isDark ? 'text-[#FAFAF7]' : 'text-[#1A1A1A]'}`}>{option.name}</h3>
                        <p className={`mt-1 text-sm ${isDark ? 'text-[#F5E8EA]' : 'text-[#5A5A5A]'}`}>{option.cadence}</p>
                      </div>
                      <p className={`font-serif text-2xl font-semibold ${isDark ? 'text-[#FAFAF7]' : 'text-[#9F1D35]'}`}>{option.price}</p>
                    </div>
                    <p className={`text-sm leading-6 ${isDark ? 'text-[#F5E8EA]' : 'text-[#5A5A5A]'}`}>{option.summary}</p>
                    <button
                      type="button"
                      onClick={() => setExpandedPackage(expanded ? '' : option.name)}
                      className={`mt-4 flex w-full items-center justify-between rounded-full border px-4 py-2 text-sm font-medium ${isDark ? 'border-[#FAFAF7]/30 text-[#FAFAF7]' : 'border-[#9F1D35] text-[#9F1D35]'}`}
                    >
                      View details
                      <ChevronDown className={`h-4 w-4 transition-transform ${expanded ? 'rotate-180' : ''}`} />
                    </button>
                    {expanded && (
                      <div className="mt-4 space-y-2">
                        {option.included.map(item => (
                          <p key={item} className={`flex items-center gap-2 text-sm ${isDark ? 'text-[#F5E8EA]' : 'text-[#1A1A1A]'}`}>
                            <CheckCircle2 className="h-4 w-4 text-green-600" />
                            {item}
                          </p>
                        ))}
                        {option.excluded.map(item => (
                          <p key={item} className={`flex items-center gap-2 text-sm ${isDark ? 'text-[#F5E8EA]/80' : 'text-[#5A5A5A]'}`}>
                            <XCircle className="h-4 w-4 text-red-500" />
                            {item}
                          </p>
                        ))}
                      </div>
                    )}
                    <Button
                      type="button"
                      variant={selected ? 'default' : isDark ? 'outline' : 'secondary'}
                      onClick={() => updateField(field.key, option.name)}
                      className={`mt-5 w-full ${isDark && !selected ? 'border-[#FAFAF7] text-[#FAFAF7] hover:bg-[#FAFAF7]/10' : ''}`}
                    >
                      {selected ? 'Selected' : `Choose ${option.name}`}
                    </Button>
                  </div>
                </article>
              );
            })}
          </div>
          {isMissing && <p className="text-sm text-red-600">Choose a care package to continue.</p>}
        </div>
      );
    }

    if (section.title === 'Package' && user?.role === 'subscriber' && field.key !== 'selectedPackage' && !form.selectedPackage) {
      return null;
    }

    if (field.type === 'state') {
      return (
        <div className="space-y-2" key={field.key}>
          <Label>{field.label}{field.required ? ' *' : ''}</Label>
          <Select value={value || ''} onValueChange={nextValue => updateField(field.key, nextValue)}>
            <SelectTrigger className={commonClass}>
              <SelectValue placeholder="Select state" />
            </SelectTrigger>
            <SelectContent>
              {NIGERIA_STATES.map(option => (
                <SelectItem key={option} value={option}>{option}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {field.hint && <p className="text-xs text-[#5A5A5A]">{field.hint}</p>}
        </div>
      );
    }

    if (field.type === 'lga') {
      const lgas = getLgasForState(form.state);
      return (
        <div className="space-y-2" key={field.key}>
          <Label>{field.label}{field.required ? ' *' : ''}</Label>
          <Select value={value || ''} onValueChange={nextValue => updateField(field.key, nextValue)} disabled={!form.state || lgas.length === 0}>
            <SelectTrigger className={commonClass}>
              <SelectValue placeholder={form.state ? 'Select LGA' : 'Select state first'} />
            </SelectTrigger>
            <SelectContent>
              {lgas.map(option => (
                <SelectItem key={option} value={option}>{option}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {field.hint && <p className="text-xs text-[#5A5A5A]">{field.hint}</p>}
        </div>
      );
    }

    if (field.type === 'select') {
      return (
        <div className="space-y-2" key={field.key}>
          <Label>{field.label}{field.required ? ' *' : ''}</Label>
          <Select value={value || ''} onValueChange={nextValue => updateField(field.key, nextValue)}>
            <SelectTrigger className={commonClass}>
              <SelectValue placeholder="Select" />
            </SelectTrigger>
            <SelectContent>
              {field.options.map(option => (
                <SelectItem key={option} value={option}>{option}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {field.hint && <p className="text-xs text-[#5A5A5A]">{field.hint}</p>}
        </div>
      );
    }

    if (field.type === 'textarea') {
      return (
        <div className="space-y-2 sm:col-span-2" key={field.key}>
          <Label>{field.label}{field.required ? ' *' : ''}</Label>
          <Textarea value={value || ''} onChange={event => updateField(field.key, event.target.value)} className={commonClass} />
          {field.hint && <p className="text-xs text-[#5A5A5A]">{field.hint}</p>}
        </div>
      );
    }

    if (field.type === 'checkbox') {
      return (
        <label key={field.key} className={`flex items-start gap-3 rounded-lg border ${isMissing ? 'border-red-300' : 'border-[#E8E6E3]'} bg-[#FAFAF7] p-4 text-sm text-[#1A1A1A] sm:col-span-2`}>
          <Checkbox checked={Boolean(value)} onCheckedChange={checked => updateField(field.key, Boolean(checked))} />
          <span>{field.label}{field.required ? ' *' : ''}</span>
        </label>
      );
    }

    if (field.type === 'file') {
      const uploaded = Boolean(value?.fileUrl);
      return (
        <div className="space-y-2 sm:col-span-2" key={field.key}>
          <Label>{field.label}{field.required ? ' *' : ''}</Label>
          <div className={`rounded-lg border ${isMissing ? 'border-red-300' : 'border-[#E8E6E3]'} bg-[#FAFAF7] p-4`}>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-3">
                <div className={`flex h-10 w-10 items-center justify-center rounded-full ${uploaded ? 'bg-green-100 text-green-700' : 'bg-[#F5E8EA] text-[#9F1D35]'}`}>
                  <FileCheck2 className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-sm font-medium text-[#1A1A1A]">{uploaded ? value.fileName : 'No file uploaded yet'}</p>
                  <p className="text-xs text-[#5A5A5A]">JPG, PNG, or PDF. Max 5MB.</p>
                </div>
              </div>
              <Input type="file" accept=".jpg,.jpeg,.png,.pdf" onChange={event => uploadFile(field, event.target.files?.[0])} className="sm:max-w-[260px]" />
            </div>
            {uploading[field.key] && (
              <div className="mt-3">
                <Progress value={uploadProgress[field.key] || 15} />
              </div>
            )}
          </div>
          {field.hint && <p className="text-xs text-[#5A5A5A]">{field.hint}</p>}
        </div>
      );
    }

    return (
      <div className="space-y-2" key={field.key}>
        <Label>{field.label}{field.required ? ' *' : ''}</Label>
        <Input
          type={field.type || 'text'}
          value={value ?? ''}
          min={field.min}
          max={field.max}
          disabled={field.disabled}
          onChange={event => updateField(field.key, event.target.value)}
          className={commonClass}
        />
        {field.hint && <p className="text-xs text-[#5A5A5A]">{field.hint}</p>}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#F7F4EE] text-[#5A5A5A]">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
        Loading KYC...
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F7F4EE] px-4 py-8">
      <div className="mx-auto max-w-6xl">
        <div className="mb-8 flex items-center justify-between">
          <Link to="/">
            <LogoLockup size="md" />
          </Link>
          <span className="rounded-full bg-[#F5E8EA] px-3 py-1 text-xs font-medium text-[#9F1D35]">
            {kycStatus.replace('_', ' ')}
          </span>
        </div>

        <div className="mb-6 overflow-hidden rounded-2xl border border-[#E8E6E3] bg-[#FAFAF7] p-5">
          <div className="mb-5 flex items-start gap-3">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#F5E8EA] text-[#9F1D35]">
              <ShieldCheck className="h-5 w-5" />
            </div>
            <div>
              <h1 className="font-serif text-3xl font-medium text-[#1A1A1A]">{config.title}</h1>
              <p className="mt-1 text-sm text-[#5A5A5A]">Step {currentStep + 1} of {sections.length}. This helps Magnolia serve your family safely and clearly.</p>
            </div>
          </div>
          <div className="landing-scroll -mx-2 flex items-center overflow-x-auto px-2 pb-1">
            {sections.map((item, index) => {
              const active = index === currentStep;
              const complete = completedSteps[index];
              return (
                <React.Fragment key={item.title}>
                  <button
                    type="button"
                    onClick={() => setCurrentStep(index)}
                    className="flex min-w-[84px] flex-col items-center gap-2 text-center"
                    aria-current={active ? 'step' : undefined}
                  >
                    <span className={`flex h-9 w-9 items-center justify-center rounded-full border text-sm font-semibold transition-colors ${
                      active
                        ? 'border-[#9F1D35] bg-[#9F1D35] text-[#FAFAF7]'
                        : complete
                          ? 'border-green-600 bg-green-50 text-green-700'
                          : 'border-[#D8D5D0] bg-white text-[#5A5A5A]'
                    }`}>
                      {complete ? <CheckCircle2 className="h-4 w-4" /> : index + 1}
                    </span>
                    <span className={`max-w-[90px] text-[11px] font-medium leading-4 ${active ? 'text-[#9F1D35]' : 'text-[#5A5A5A]'}`}>{item.title}</span>
                  </button>
                  {index < sections.length - 1 && (
                    <span className={`mb-7 h-px min-w-8 ${completedSteps[index] ? 'bg-green-500' : 'bg-[#D8D5D0]'}`} />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        <div>
          <main className="rounded-2xl border border-[#E8E6E3] bg-white p-5 sm:p-6">
            <div className="mb-6">
              <p className="text-xs font-medium uppercase tracking-[0.08em] text-[#9F1D35]">Step {currentStep + 1} of {sections.length}</p>
              <h2 className="mt-2 font-serif text-3xl font-medium text-[#1A1A1A]">{section.title}</h2>
            </div>

            {error && (
              <div className="mb-5 rounded-lg border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
                <p>{error}</p>
                {missingFields.length > 0 && <p className="mt-2 text-xs">{missingFields.join(', ')}</p>}
              </div>
            )}

            {submittedMessage && (
              <div className="mb-5 flex gap-3 rounded-lg border border-green-100 bg-green-50 px-4 py-3 text-sm text-green-800">
                <CheckCircle2 className="h-5 w-5 shrink-0" />
                <span>{submittedMessage}</span>
              </div>
            )}

            <div className="grid gap-4 sm:grid-cols-2">
              {section.fields.map(renderField)}
            </div>

            <div className="mt-8 flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
              <Button type="button" variant="outline" onClick={() => setCurrentStep(step => Math.max(step - 1, 0))} disabled={currentStep === 0 || saving || submitting}>
                <ArrowLeft className="h-4 w-4" />
                Back
              </Button>

              {currentStep < sections.length - 1 ? (
                <Button type="button" onClick={() => saveSection()} disabled={saving || submitting}>
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  Save & Continue
                </Button>
              ) : (
                <Button type="button" onClick={handleSubmit} disabled={submitting || saving || kycStatus === 'submitted'}>
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  Submit KYC
                </Button>
              )}
            </div>
          </main>
        </div>
      </div>
    </div>
  );
};

export default KycPage;
